import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import ExpandableText from "../components/ExpandableText";
import { takvimKategorileri } from "../utils/constants";
import { listLabel } from "../utils/i18nOptions";
import ReminderPanel, { ReminderWarning, reminderPayloadKontrolEt } from "../components/ReminderPanel";
import AvatarImg from "../components/AvatarImg";
import AsyncActionButton from "../components/AsyncActionButton";
import FormValidationNotice, { useFormValidation } from "../components/FormValidationNotice";
import gorevlerAsset from "../assets/gorevlerasset.png";
import { appConfirm } from "../components/AppDialog";

function TasksPage({
  gorevFormAcik, setGorevFormAcik, uyeler,
  gorevAdi, setGorevAdi, gorevAciklama, setGorevAciklama,
  gorevAtananlar, setGorevAtananlar, gorevSonTarih, setGorevSonTarih,
  gorevOncelik, setGorevOncelik, gorevKategori, setGorevKategori,
  gorevEkle,
  gorevFiltresi, setGorevFiltresi, filtreliGorevler, tumGorevler = [],
  gecikmisMi, gorevTamamla, gorevSil,
  permissions, user,
  onReminderKur,
}) {
  const { t, tList, lang } = useLanguage();
  const taskValidation = useFormValidation("task-create");

  const AYLAR = tList("common.months");

  // Kategori listesi: takvim kategorileri + görev özel kategoriler
  const extraCats = tList("const.taskExtraCategories");
  const GOREV_KAT_LISTESI = [...new Set([...takvimKategorileri, ...extraCats])];
  const gorevKategoriEtiketi = (k) => {
    const calendarLabel = listLabel(tList, "const.calendarCategories", k);
    if (calendarLabel !== k) return calendarLabel;
    return listLabel(tList, "const.taskExtraCategories", k);
  };

  const oncelikCanonical = (value) => {
    const priorityMap = {
      "Yüksek": "Yüksek",
      "Normal": "Normal",
      "Düşük": "Düşük",
      [t("tasks.priority.high")]: "Yüksek",
      [t("tasks.priority.normal")]: "Normal",
      [t("tasks.priority.low")]: "Düşük",
      "High": "Yüksek",
      "Low": "Düşük",
    };
    return priorityMap[value] || "Normal";
  };

  const oncelikEtiketi = (value) => {
    const canonical = oncelikCanonical(value);
    if (canonical === "Yüksek") return t("tasks.priority.high");
    if (canonical === "Düşük") return t("tasks.priority.low");
    return t("tasks.priority.normal");
  };

  const filtreCanonical = (value) => {
    const filterMap = {
      "Tümü": "Tümü",
      "Bana Atanan": "Bana Atanan",
      "Geciken": "Geciken",
      "Tamamlanan": "Tamamlanan",
      [t("tasks.filter.all")]: "Tümü",
      [t("tasks.filter.mine")]: "Bana Atanan",
      [t("tasks.filter.late")]: "Geciken",
      [t("tasks.filter.done")]: "Tamamlanan",
      "All": "Tümü",
      "Assigned to Me": "Bana Atanan",
      "Assigned to me": "Bana Atanan",
      "Overdue": "Geciken",
      "Completed": "Tamamlanan",
    };
    return filterMap[value] || value || "Tümü";
  };

  function tarihOkunakli(str) {
    if (!str) return "";
    const [y, m, d] = str.split("-");
    return `${parseInt(d)} ${AYLAR[parseInt(m) - 1]} ${y}`;
  }

  const canAdd         = permissions?.actions?.addTask;
  const canCompleteOwn = permissions?.actions?.completeOwnTask;
  const canCompleteAll = permissions?.actions?.completeAllTasks;
  const canDeleteOwn   = permissions?.actions?.deleteOwnTask;
  const canDeleteAll   = permissions?.actions?.deleteAllTasks;

  const [reminderAktif,  setReminderAktif]  = useState(false);
  const [reminderTarih,  setReminderTarih]  = useState("");
  const [reminderSaat,   setReminderSaat]   = useState("09:00");
  const [reminderTekrar, setReminderTekrar] = useState("none");
  const [reminderError, setReminderError] = useState("");
  const [taskAssigneeOpen, setTaskAssigneeOpen] = useState(false);
  const [taskCreating, setTaskCreating] = useState(false);
  const [taskUpdatingId, setTaskUpdatingId] = useState(null);
  const [completionPulse, setCompletionPulse] = useState(false);
  const previousCompletionRef = useRef({ total: 0, done: 0 });
  const taskFormBodyRef = useRef(null);
  const taskReminderRef = useRef(null);

  useEffect(() => {
    if (!gorevFormAcik || !reminderAktif) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const body = taskFormBodyRef.current;
      const reminder = taskReminderRef.current;
      if (!body || !reminder) return;

      const targetBottom = reminder.offsetTop + reminder.offsetHeight;
      const visibleBottom = body.scrollTop + body.clientHeight;
      if (targetBottom > visibleBottom) {
        body.scrollTo({
          top: Math.max(0, targetBottom - body.clientHeight + 12),
          behavior: "smooth",
        });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [gorevFormAcik, reminderAktif]);

  const taskAssigneeText = {
    allFamily: lang === "en" ? "All family" : "Tüm aile",
    allFamilyDesc: lang === "en" ? "Assign this task to every family member." : "Bu görevi tüm aile üyelerine ata.",
    selected: lang === "en" ? "selected" : "seçili",
    chooseTitle: lang === "en" ? "Assigned family members" : "Atanan aile üyeleri",
    closedHint: lang === "en" ? "Tap to choose assignees" : "Atanan kişileri seçmek için dokun",
    close: lang === "en" ? "Close" : "Kapat",
    personal: lang === "en" ? "Personal" : "Kişisel",
    roleFather: lang === "en" ? "Father" : "Baba",
    roleMother: lang === "en" ? "Mother" : "Anne",
    roleChild: lang === "en" ? "Child" : "Çocuk",
    roleUncle: lang === "en" ? "Uncle" : "Amca",
    roleAuntPaternal: lang === "en" ? "Paternal aunt" : "Hala",
    roleAuntMaternal: lang === "en" ? "Aunt" : "Teyze",
    roleMaternalUncle: lang === "en" ? "Maternal uncle" : "Dayı",
    roleGrandmother: lang === "en" ? "Grandmother" : "Nene",
    roleGrandfather: lang === "en" ? "Grandfather" : "Dede",
    roleOther: lang === "en" ? "Other" : "Diğer",
  };

  const taskNormalizeRole = (u = {}) => {
    const raw = String(
      u.familyRole || u.family_role || u.aileRolu || u.aile_rolu ||
      u.rol || u.role || u.profilRol || u.yakinlik || u.relationship || u.unvan || u.title || ""
    ).toLocaleLowerCase("tr-TR").trim();

    if (raw.includes("baba") || raw.includes("father") || raw === "dad") return "father";
    if (raw.includes("anne") || raw.includes("mother") || raw === "mom") return "mother";
    if (raw.includes("çocuk") || raw.includes("cocuk") || raw.includes("child") || raw.includes("bebek") || raw.includes("baby") || raw.includes("kız") || raw.includes("kiz") || raw.includes("erkek")) return "child";
    if (raw.includes("amca")) return "uncle";
    if (raw.includes("hala")) return "auntPaternal";
    if (raw.includes("teyze") || raw === "aunt") return "auntMaternal";
    if (raw.includes("dayı") || raw.includes("dayi")) return "maternalUncle";
    if (raw.includes("nene") || raw.includes("nine") || raw.includes("babaanne") || raw.includes("anneanne") || raw.includes("grandmother")) return "grandmother";
    if (raw.includes("dede") || raw.includes("grandfather")) return "grandfather";
    if (raw.includes("kişisel") || raw.includes("kisisel") || raw.includes("personal") || raw === "me") return "personal";
    return "other";
  };

  const taskRoleOrder = { personal: 0, father: 1, mother: 2, child: 3, uncle: 4, auntPaternal: 5, auntMaternal: 6, maternalUncle: 7, grandmother: 8, grandfather: 9, other: 10 };
  const taskRoleLabel = (role) => ({
    personal: taskAssigneeText.personal,
    father: taskAssigneeText.roleFather,
    mother: taskAssigneeText.roleMother,
    child: taskAssigneeText.roleChild,
    uncle: taskAssigneeText.roleUncle,
    auntPaternal: taskAssigneeText.roleAuntPaternal,
    auntMaternal: taskAssigneeText.roleAuntMaternal,
    maternalUncle: taskAssigneeText.roleMaternalUncle,
    grandmother: taskAssigneeText.roleGrandmother,
    grandfather: taskAssigneeText.roleGrandfather,
    other: taskAssigneeText.roleOther,
  }[role] || taskAssigneeText.roleOther);

  const taskMemberId = (u = {}) => String(u.uid || u.id || "");
  const taskMemberName = (u = {}) => `${u.ad || u.name || ""} ${u.soyad || u.surname || ""}`.trim() || u.displayName || u.email || taskRoleLabel(taskNormalizeRole(u));
  const taskMemberInitials = (u = {}) => {
    const name = taskMemberName(u);
    const parts = name.split(/\s+/).filter(Boolean);
    return ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toLocaleUpperCase("tr-TR") || "?";
  };

  const siraliUyeler = useMemo(() => [...(uyeler || [])]
    .filter((u) => taskMemberId(u))
    .sort((a, b) => {
      const ra = taskNormalizeRole(a);
      const rb = taskNormalizeRole(b);
      const rank = (taskRoleOrder[ra] ?? 99) - (taskRoleOrder[rb] ?? 99);
      if (rank !== 0) return rank;
      return taskMemberName(a).localeCompare(taskMemberName(b), lang === "en" ? "en" : "tr");
    }), [uyeler, lang]);

  const seciliGorevUyeleri = useMemo(() => {
    const selectedSet = new Set((gorevAtananlar || []).map(String));
    return siraliUyeler.filter((u) => selectedSet.has(taskMemberId(u)));
  }, [gorevAtananlar, siraliUyeler]);

  const gorevAtananOzeti = useMemo(() => {
    if (!seciliGorevUyeleri.length) return taskAssigneeText.closedHint;
    if (seciliGorevUyeleri.length === siraliUyeler.length && siraliUyeler.length > 0) return taskAssigneeText.allFamily;
    if (seciliGorevUyeleri.length <= 2) return seciliGorevUyeleri.map(taskMemberName).join(", ");
    return `${seciliGorevUyeleri.length} ${taskAssigneeText.selected}`;
  }, [seciliGorevUyeleri, siraliUyeler.length, taskAssigneeText.closedHint, taskAssigneeText.allFamily, taskAssigneeText.selected]);

  const gorevTumAileMi = (g = {}) => {
    if (g?.atananScope === "all_family" || g?.assigneeScope === "all_family" || g?.isAllFamily === true) return true;
    const atananUids = Array.isArray(g?.atananUids) ? g.atananUids.map(String).filter(Boolean) : [];
    return siraliUyeler.length > 1 && atananUids.length >= siraliUyeler.length && siraliUyeler.every((u) => atananUids.includes(taskMemberId(u)));
  };

  const gorevAtananKartMetni = (g = {}) => {
    if (gorevTumAileMi(g)) return `👨‍👩‍👧‍👦 ${taskAssigneeText.allFamily}`;
    if (Array.isArray(g.atananAdlar) && g.atananAdlar.length > 1) return g.atananAdlar.join(", ");
    return g.atananAd || "";
  };


  const handleGorevEkle = async () => {
    if (taskCreating) return;
    if (!taskValidation.validate([
      !gorevAdi?.trim() && "name",
      !(gorevAtananlar || []).length && "assignees",
      !gorevSonTarih && "dueDate",
    ])) return;
    setTaskCreating(true);

    try {
      let reminderPayload = null;
      if (reminderAktif && onReminderKur) {
        const bazTarih = reminderTarih || gorevSonTarih;
        reminderPayload = reminderPayloadKontrolEt({
          mod: "datetime",
          tarih: bazTarih,
          saat: reminderSaat,
          tekrar: reminderTekrar,
        }, t, lang, setReminderError);
        if (!reminderPayload) return;
      }

      const yeniGorevId = await gorevEkle();
      if (!yeniGorevId) return false;

      if (reminderPayload && onReminderKur) {
        await onReminderKur({
          baslik: t("tasks.reminderTitle"),
          aciklama: gorevAdi?.trim()
            ? t("notify.taskReminder.item", { task: gorevAdi.trim() })
            : t("notify.taskReminder.default"),
          baslikKey: "tasks.reminderTitle",
          aciklamaKey: gorevAdi?.trim() ? "notify.taskReminder.item" : "notify.taskReminder.default",
          aciklamaVars: gorevAdi?.trim() ? { task: gorevAdi.trim() } : null,
          kaynak: "task",
          kaynakId: yeniGorevId,
          kaynakTuru: "task",
          url: "/?pushPage=gorev",
          ...reminderPayload,
        }).catch(() => {});
      }

      setReminderAktif(false);
      setReminderTarih("");
      setReminderSaat("09:00");
      setReminderTekrar("none");
      setReminderError("");
      setTaskAssigneeOpen(false);
      setGorevFormAcik(false);
      return true;
    } finally {
      setTaskCreating(false);
    }
  };

  const tarihDegeri = (g) => {
    const createdAt = g.createdAt;
    if (createdAt?.toMillis) return createdAt.toMillis();
    if (createdAt?.seconds) return createdAt.seconds * 1000;
    if (createdAt) {
      const parsed = new Date(createdAt).getTime();
      if (!Number.isNaN(parsed)) return parsed;
    }
    if (g.sonTarih) {
      const parsed = new Date(g.sonTarih).getTime();
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 0;
  };

  const gorevKullaniciyaAtanmisMi = (g) => {
    const mevcutUid = String(user?.uid || "");
    const atananUid = String(
      g?.atananUid ||
      g?.atananId ||
      g?.assignedToUid ||
      g?.assigneeUid ||
      g?.memberUid ||
      ""
    );

    if (mevcutUid && atananUid && atananUid === mevcutUid) return true;
    const atananUids = Array.isArray(g?.atananUids) ? g.atananUids.map(String) : [];
    if (mevcutUid && atananUids.includes(mevcutUid)) return true;

    const mevcutEmail = String(user?.email || "").trim().toLowerCase();
    const atananEmail = String(g?.atananEmail || g?.assignedToEmail || "").trim().toLowerCase();

    return Boolean(mevcutEmail && atananEmail && atananEmail === mevcutEmail);
  };

  const sirali = [...filtreliGorevler].sort((a, b) => {
    if (filtreCanonical(gorevFiltresi) === "Tümü" && a.tamamlandi !== b.tamamlandi) {
      return a.tamamlandi ? 1 : -1;
    }
    return tarihDegeri(b) - tarihDegeri(a);
  });

  const gorunenGorevler = sirali;
  const oncelikRenk = {
    "Yüksek": "oncelikYuksek",
    "Normal": "oncelikNormal",
    "Düşük": "oncelikDusuk",
  };


  const allTasks = Array.isArray(tumGorevler) && tumGorevler.length ? tumGorevler : filtreliGorevler;
  const taskCounts = (() => {
    const total = allTasks.length;
    const done = allTasks.filter((g) => g.tamamlandi).length;
    const late = allTasks.filter((g) => gecikmisMi(g)).length;
    const mine = allTasks.filter((g) => gorevKullaniciyaAtanmisMi(g) && !g.tamamlandi).length;
    return { total, done, late, mine, active: Math.max(0, total - done) };
  })();

  const completionPercent = taskCounts.total ? Math.round((taskCounts.done / taskCounts.total) * 100) : 0;
  const allTasksCompleted = taskCounts.total > 0 && taskCounts.active === 0;

  const taskProgressMessage = taskCounts.total === 0
    ? t("tasks.progress.start")
    : allTasksCompleted
      ? t(taskCounts.done === 1 ? "tasks.progress.doneOne" : "tasks.progress.done", { count: taskCounts.done })
      : t(taskCounts.active === 1 ? "tasks.progress.pendingOne" : "tasks.progress.pending", { count: taskCounts.active });

  useEffect(() => {
    const previous = previousCompletionRef.current;
    const justCompletedAll = taskCounts.total > 0
      && taskCounts.done === taskCounts.total
      && (previous.done < previous.total || previous.total !== taskCounts.total);

    previousCompletionRef.current = { total: taskCounts.total, done: taskCounts.done };

    if (!justCompletedAll) return undefined;
    setCompletionPulse(true);
    const timer = window.setTimeout(() => setCompletionPulse(false), 1800);
    return () => window.clearTimeout(timer);
  }, [taskCounts.total, taskCounts.done]);

  const handleTaskToggle = async (task) => {
    if (!task || taskUpdatingId) return;
    setTaskUpdatingId(task.id);
    try {
      await Promise.resolve(gorevTamamla(task));
    } finally {
      window.setTimeout(() => setTaskUpdatingId(null), 180);
    }
  };

  const handleTaskDelete = async (task) => {
    if (!task) return;
    const confirmed = await appConfirm(t("tasks.deleteConfirm"), {
      title: t("tasks.deleteTitle"),
      confirmText: t("tasks.deleteYes"),
      cancelText: t("common.cancel"),
      danger: true,
    });
    if (!confirmed) return;
    await Promise.resolve(gorevSil(task));
  };

  const filterCount = (value) => ({
    "Tümü": taskCounts.total,
    "Bana Atanan": taskCounts.mine,
    "Geciken": taskCounts.late,
    "Tamamlanan": taskCounts.done,
  }[value] ?? 0);

  const taskAssigneesForCard = (g = {}) => {
    const ids = Array.isArray(g.atananUids) ? g.atananUids.map(String) : [String(g.atananUid || g.atananId || "")].filter(Boolean);
    return siraliUyeler.filter((u) => ids.includes(taskMemberId(u)));
  };

  const overdueDays = (g = {}) => {
    if (!g.sonTarih || !gecikmisMi(g)) return 0;
    const due = new Date(`${g.sonTarih}T23:59:59`);
    return Math.max(1, Math.floor((Date.now() - due.getTime()) / 86400000));
  };

  const emptyCopy = (() => {
    const current = filtreCanonical(gorevFiltresi);
    if (current === "Geciken") return { title: t("tasks.emptyLateTitle"), text: t("tasks.emptyLateText") };
    if (current === "Bana Atanan") return { title: t("tasks.emptyMineTitle"), text: t("tasks.emptyMineText") };
    if (current === "Tamamlanan") return { title: t("tasks.emptyDoneTitle"), text: t("tasks.emptyDoneText") };
    return { title: t("tasks.emptyTitle"), text: t("tasks.emptyText") };
  })();

  const FILTERS = [
    { value: "Tümü", label: t("tasks.filter.all") },
    { value: "Bana Atanan", label: t("tasks.filter.mine") },
    { value: "Geciken", label: t("tasks.filter.late") },
    { value: "Tamamlanan", label: t("tasks.filter.done") },
  ];

  return (
    <div className="pageContent tasksProPage">
      <style>{`
        /* Görev sayfası geçiş koruması:
           İç liste ekrana sığacak şekilde kalan alanı doldurur;
           arka plan ise animasyon/swi­pe sırasında alttan ana sayfa görünmemesi için
           ekranın aşağısına doğru daha uzun, opak bir zemin olarak uzatılır. */
        .swipePageWrap.tasksTransitionGuard {
          position: relative;
          min-height: calc(145dvh + env(safe-area-inset-bottom, 0px));
          background: linear-gradient(160deg, var(--bg-base) 0%, var(--bg-surface) 58%, var(--bg-base) 100%);
          overflow: hidden;
          box-sizing: border-box;
          isolation: isolate;
        }
        .swipePageWrap.tasksTransitionGuard::before {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          bottom: -50dvh;
          z-index: 0;
          pointer-events: none;
          background: linear-gradient(160deg, var(--bg-base) 0%, var(--bg-surface) 58%, var(--bg-base) 100%);
        }
        .swipePageWrap.tasksTransitionGuard .tasksProPage {
          position: relative;
          z-index: 1;
          background: transparent;
          box-sizing: border-box;
        }

        /* ─────────────────────────────────────────────────────────────
           SAYFA BASLIGI (HEADER) GORUNURLUGU

           Sayfa basligi (.topHeader) bu sarmalayicinin ICINDE render edilir.
           Yukaridaki .tasksTransitionGuard::before katmani konumlandirilmis ve
           z-index: 0 oldugundan, konumlandirilmamis akis icerigi olan basligin
           UZERINE cizilir. Sonuc: Gorevler sayfasinda baslik ozellikle acik
           temada kayboluyordu (koyu temada zemin rengi basliga cok yakin
           oldugu icin fark edilmiyordu).

           Basligi ayni yigin baglaminda ::before (0) ve sayfa icerigi (1)
           katmanlarinin UZERINE alarak sorunu koklu bicimde cozuyoruz.
           Kural yalnizca Gorevler sayfasinin sarmalayicisini hedefler; diger
           sayfalar etkilenmez. iOS, web ve Android'de ayni sekilde calisir.
        ───────────────────────────────────────────────────────────── */
        .swipePageWrap.tasksTransitionGuard > .topHeader {
          position: relative;
          z-index: 2;
        }

        .tasksProPage {
          height: calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 88px);
          max-height: calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 88px);
          display: flex;
          flex-direction: column;
          gap: 8px;
          overflow: hidden;
          padding-bottom: 0;
        }

        .tasksProPage .categoryChips {
          flex-shrink: 0;
          gap: 6px;
          padding: 2px 0 8px;
          margin: 0;
        }

        .tasksProPage .chip {
          padding: 6px 11px;
          font-size: 12px;
        }

        .tasksProPage .tasksAddCard {
          flex-shrink: 0;
          padding: 10px 12px;
          margin-bottom: 0;
          border-radius: 16px;
        }

        .tasksProPage .tasksAddCard .cardHeaderTitle {
          font-size: 14px;
        }

        .tasksProPage .tasksAddCard .miniToggle {
          width: 28px;
          height: 28px;
          font-size: 16px;
        }



        .tasksProPage .taskCreatePremiumCard {
          position: relative;
          flex-shrink: 0;
          margin: 0;
          border-radius: 18px;
          overflow: hidden;
          background: var(--card-bg, rgba(255,255,255,0.90));
          border: 1px solid var(--border-color, rgba(148, 163, 184, 0.20));
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.07);
        }

        body.dark .tasksProPage .taskCreatePremiumCard,
        [data-theme="dark"] .tasksProPage .taskCreatePremiumCard,
        body[data-theme="dark"] .tasksProPage .taskCreatePremiumCard,
        body[data-tema="dark"] .tasksProPage .taskCreatePremiumCard {
          background: var(--card-bg, rgba(15, 23, 42, 0.72));
          border-color: rgba(148, 163, 184, 0.18);
          box-shadow: 0 12px 28px rgba(0, 0, 0, 0.18);
        }

        .tasksProPage .taskCreatePremiumHeader {
          width: 100%;
          border: 0;
          background: transparent;
          display: grid;
          grid-template-columns: 38px minmax(0, 1fr) 26px;
          align-items: center;
          gap: 10px;
          min-height: 58px;
          padding: 10px 12px;
          color: var(--text-main, #111827);
          text-align: left;
          cursor: pointer;
        }

        .tasksProPage .taskCreatePremiumIcon {
          width: 38px;
          height: 38px;
          border-radius: 50%;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          background: #1e3a8a;
          color: #fff;
          font-size: 24px;
          font-weight: 700;
          line-height: 1;
          box-shadow: 0 10px 18px rgba(30, 58, 138, 0.24);
        }

        .tasksProPage .taskCreatePremiumTitle {
          min-width: 0;
          color: var(--text-main, #0f172a);
          font-size: 14px;
          font-weight: 800;
          letter-spacing: -0.01em;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        body.dark .tasksProPage .taskCreatePremiumTitle,
        [data-theme="dark"] .tasksProPage .taskCreatePremiumTitle,
        body[data-theme="dark"] .tasksProPage .taskCreatePremiumTitle,
        body[data-tema="dark"] .tasksProPage .taskCreatePremiumTitle {
          color: rgba(248, 250, 252, 0.96);
        }

        .tasksProPage .taskCreatePremiumArrow {
          color: #3b82f6;
          font-size: 22px;
          font-weight: 900;
          text-align: center;
          line-height: 1;
        }

        .tasksProPage .taskCreatePremiumCard.open {
          padding-bottom: 12px;
          max-height: min(72dvh, 640px);
          display: flex;
          flex-direction: column;
        }

        .tasksProPage .taskCreatePremiumCard.open .taskCreatePremiumHeader {
          flex: 0 0 auto;
        }

        .tasksProPage .taskCreatePremiumBody {
          margin: 0 12px;
          padding: 0 2px 2px 0;
        }

        .tasksProPage .taskCreatePremiumCard.open .taskCreatePremiumBody {
          flex: 1 1 auto;
          min-height: 0;
          overflow-y: auto;
          overflow-x: hidden;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          touch-action: pan-y;
          scrollbar-width: none;
          scroll-padding-block: 10px 18px;
          padding-bottom: max(10px, env(safe-area-inset-bottom, 0px));
        }

        .tasksProPage .taskCreatePremiumCard.open .taskCreatePremiumBody::-webkit-scrollbar { display: none; }

        .tasksProPage .tasksCompactForm {
          margin-top: 8px;
          gap: 7px;
        }

        .tasksProPage .tasksCompactForm .formRow {
          gap: 7px;
        }

        .tasksProPage .tasksCompactForm .darkLabel {
          font-size: 9px;
          margin-bottom: 3px;
          letter-spacing: 0.25px;
        }

        .tasksProPage .tasksCompactForm .darkInput {
          min-height: 40px;
          padding: 10px 11px;
          border-radius: 12px;
          font-size: 13px;
        }

        .tasksProPage .tasksCompactForm textarea.darkInput {
          min-height: 52px;
          resize: none;
          line-height: 1.28;
        }

        .tasksProPage .tasksCompactForm .formRow .inputLabelGroup input.darkInput,
        .tasksProPage .tasksCompactForm .formRow .inputLabelGroup select.darkInput {
          height: 40px;
          min-height: 40px;
          padding-top: 0;
          padding-bottom: 0;
        }

        .tasksProPage .tasksCompactForm .primaryButton {
          min-height: 40px;
          padding: 10px 12px;
          margin-top: 2px;
          font-size: 13px;
          border-radius: 13px;
        }

        .tasksProPage .gorevSaglikHint {
          font-size: 11px;
          line-height: 1.25;
          margin-top: 4px;
          padding: 7px 9px;
          border-radius: 11px;
          background: rgba(124, 58, 237, 0.10);
          color: var(--text-muted);
        }

        .tasksProPage .gorevAtananSecici {
          margin-top: 6px;
          border-radius: 20px;
          overflow: hidden;
          background:
            radial-gradient(circle at 0% 0%, rgba(99, 102, 241, 0.16), transparent 34%),
            radial-gradient(circle at 100% 20%, rgba(20, 184, 166, 0.12), transparent 32%),
            linear-gradient(145deg, rgba(255,255,255,0.88), rgba(248,250,252,0.76));
          border: 1px solid rgba(15, 23, 42, 0.08);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.72), 0 12px 26px rgba(15,23,42,0.07);
        }

        body.dark .tasksProPage .gorevAtananSecici,
        [data-theme="dark"] .tasksProPage .gorevAtananSecici,
        body[data-theme="dark"] .tasksProPage .gorevAtananSecici,
        body[data-tema="dark"] .tasksProPage .gorevAtananSecici {
          background:
            radial-gradient(circle at 0% 0%, rgba(124, 58, 237, 0.22), transparent 34%),
            radial-gradient(circle at 100% 20%, rgba(14, 165, 233, 0.16), transparent 32%),
            linear-gradient(145deg, rgba(15,23,42,0.90), rgba(30,41,59,0.78));
          border-color: rgba(255,255,255,0.12);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06), 0 16px 34px rgba(0,0,0,0.20);
        }

        .tasksProPage .gorevAtananToggle {
          width: 100%;
          min-height: 56px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto 22px;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border: 0;
          background: transparent;
          color: var(--text-main, #0f172a);
          text-align: left;
          cursor: pointer;
        }

        .tasksProPage .gorevAtananToggleLeft {
          display: flex;
          flex-direction: column;
          gap: 2px;
          min-width: 0;
        }

        .tasksProPage .gorevAtananToggleTitle {
          color: var(--text-main, #0f172a);
          font-size: 12px;
          font-weight: 900;
          letter-spacing: -0.01em;
        }

        .tasksProPage .gorevAtananToggleSummary {
          color: var(--text-muted, #64748b);
          font-size: 11px;
          font-weight: 750;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        body.dark .tasksProPage .gorevAtananToggleTitle,
        [data-theme="dark"] .tasksProPage .gorevAtananToggleTitle,
        body[data-theme="dark"] .tasksProPage .gorevAtananToggleTitle,
        body[data-tema="dark"] .tasksProPage .gorevAtananToggleTitle { color: rgba(248,250,252,0.96); }

        body.dark .tasksProPage .gorevAtananToggleSummary,
        [data-theme="dark"] .tasksProPage .gorevAtananToggleSummary,
        body[data-theme="dark"] .tasksProPage .gorevAtananToggleSummary,
        body[data-tema="dark"] .tasksProPage .gorevAtananToggleSummary { color: rgba(203,213,225,0.76); }

        .tasksProPage .gorevAtananToggleAvatars {
          display: inline-flex;
          align-items: center;
          justify-content: flex-end;
          min-width: 54px;
        }

        .tasksProPage .gorevAtananMiniAvatar {
          width: 25px;
          height: 25px;
          border-radius: 50%;
          overflow: hidden;
          margin-left: -7px;
          border: 2px solid rgba(255,255,255,0.78);
          box-shadow: 0 4px 10px rgba(15,23,42,0.14);
          background: rgba(148,163,184,0.20);
        }

        body.dark .tasksProPage .gorevAtananMiniAvatar,
        [data-theme="dark"] .tasksProPage .gorevAtananMiniAvatar,
        body[data-theme="dark"] .tasksProPage .gorevAtananMiniAvatar,
        body[data-tema="dark"] .tasksProPage .gorevAtananMiniAvatar { border-color: rgba(15,23,42,0.82); }

        .tasksProPage .gorevAtananMiniMore {
          min-width: 25px;
          height: 25px;
          margin-left: -7px;
          padding: 0 6px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #fff;
          background: linear-gradient(135deg, #6366f1, #7c3aed);
          border: 2px solid rgba(255,255,255,0.80);
          font-size: 10px;
          font-weight: 950;
          box-shadow: 0 4px 10px rgba(99,102,241,0.22);
        }

        .tasksProPage .gorevAtananChevron {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #6366f1;
          font-size: 20px;
          font-weight: 950;
          transition: transform 0.2s ease;
        }

        .tasksProPage .gorevAtananChevron.open { transform: rotate(180deg); }

        .tasksProPage .gorevAtananDropdown {
          display: grid;
          gap: 8px;
          padding: 0 10px 10px;
          animation: taskAssigneeDrop 0.18s ease-out;
        }

        .tasksProPage .gorevAtananMemberScroll {
          display: grid;
          gap: 7px;
          max-height: 116px;
          overflow-y: auto;
          overflow-x: hidden;
          padding-right: 2px;
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          scrollbar-width: none;
        }

        .tasksProPage .gorevAtananMemberScroll::-webkit-scrollbar { display: none; }

        @keyframes taskAssigneeDrop {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }

        .tasksProPage .gorevAtananOption {
          width: 100%;
          min-height: 52px;
          display: grid;
          grid-template-columns: 28px 38px minmax(0, 1fr) 18px;
          align-items: center;
          gap: 9px;
          border-radius: 16px;
          border: 1px solid rgba(15, 23, 42, 0.07);
          background: rgba(255,255,255,0.62);
          color: var(--text-main, #0f172a);
          padding: 8px 10px;
          text-align: left;
          cursor: pointer;
          transition: transform 0.14s ease, border-color 0.14s ease, background 0.14s ease;
        }

        .tasksProPage .gorevAtananOption:active { transform: scale(0.985); }

        .tasksProPage .gorevAtananOption.secili {
          border-color: rgba(99, 102, 241, 0.42);
          background: linear-gradient(135deg, rgba(99,102,241,0.14), rgba(20,184,166,0.08));
        }

        .tasksProPage .gorevAtananOption.all {
          grid-template-columns: 28px minmax(0, 1fr) 18px;
          background: linear-gradient(135deg, rgba(99,102,241,0.14), rgba(20,184,166,0.09));
        }

        body.dark .tasksProPage .gorevAtananOption,
        [data-theme="dark"] .tasksProPage .gorevAtananOption,
        body[data-theme="dark"] .tasksProPage .gorevAtananOption,
        body[data-tema="dark"] .tasksProPage .gorevAtananOption {
          background: rgba(15,23,42,0.48);
          border-color: rgba(255,255,255,0.09);
          color: rgba(248,250,252,0.96);
        }

        body.dark .tasksProPage .gorevAtananOption.secili,
        [data-theme="dark"] .tasksProPage .gorevAtananOption.secili,
        body[data-theme="dark"] .tasksProPage .gorevAtananOption.secili,
        body[data-tema="dark"] .tasksProPage .gorevAtananOption.secili {
          border-color: rgba(129,140,248,0.50);
          background: linear-gradient(135deg, rgba(99,102,241,0.20), rgba(20,184,166,0.10));
        }

        .tasksProPage .gorevAtananCheck,
        .tasksProPage .gorevAtananGo {
          width: 24px;
          height: 24px;
          border-radius: 999px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(100,116,139,0.13);
          color: transparent;
          font-size: 13px;
          font-weight: 950;
          border: 1px solid rgba(100,116,139,0.16);
        }

        .tasksProPage .gorevAtananOption.secili .gorevAtananCheck {
          color: #fff;
          background: linear-gradient(135deg, #6366f1, #7c3aed);
          border-color: transparent;
          box-shadow: 0 7px 16px rgba(99,102,241,0.22);
        }

        .tasksProPage .gorevAtananGo {
          color: #6366f1;
          background: transparent;
          border: 0;
          font-size: 18px;
        }

        .tasksProPage .gorevAtananAvatar {
          width: 36px;
          height: 36px;
          border-radius: 14px;
          overflow: hidden;
          box-shadow: 0 8px 18px rgba(15,23,42,0.12);
        }

        .tasksProPage .gorevAtananText {
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .tasksProPage .gorevAtananText strong {
          color: inherit;
          font-size: 12.5px;
          line-height: 1.14;
          font-weight: 900;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .tasksProPage .gorevAtananText small {
          color: var(--text-muted, #64748b);
          font-size: 10.5px;
          font-weight: 800;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        body.dark .tasksProPage .gorevAtananText small,
        [data-theme="dark"] .tasksProPage .gorevAtananText small,
        body[data-theme="dark"] .tasksProPage .gorevAtananText small,
        body[data-tema="dark"] .tasksProPage .gorevAtananText small { color: rgba(203,213,225,0.72); }

        .tasksProPage .gorevAtananBosUyari {
          display: block;
          margin-top: 5px;
          color: var(--text-muted, #64748b);
          font-size: 11.5px;
          font-weight: 700;
        }

        .tasksProPage .tasksListCard {
          flex: 1 1 auto;
          min-height: 0;
          max-height: none;
          overflow-y: auto;
          overflow-x: hidden;
          margin-bottom: max(8px, env(safe-area-inset-bottom, 0px));
          -webkit-overflow-scrolling: touch;
          overscroll-behavior: contain;
          touch-action: pan-y;
          scrollbar-width: none;
        }

        .tasksProPage .tasksListCard::-webkit-scrollbar {
          display: none;
        }

        .tasksProPage .tasksListCard .emptyState {
          min-height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 22px 14px;
          font-size: 13px;
          box-sizing: border-box;
        }

        .tasksProPage .tasksEmptyPremium {
          width: 100%;
          min-height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          box-sizing: border-box;
          padding: clamp(22px, 5vh, 46px) 22px;
        }

        .tasksProPage .tasksEmptyPremiumImage {
          width: clamp(82px, 23vw, 118px);
          height: clamp(82px, 23vw, 118px);
          object-fit: contain;
          opacity: 0.5;
          display: block;
          margin: 0 0 17px;
          flex: 0 0 auto;
        }

        .tasksProPage .tasksEmptyPremiumTitle {
          margin: 0;
          color: var(--text-main, #0f172a);
          font-size: 14px;
          line-height: 1.18;
          font-weight: 900;
          letter-spacing: -0.025em;
        }

        .tasksProPage .tasksEmptyPremiumText {
          width: min(100%, 390px);
          margin: 10px 0 0;
          color: var(--text-muted, #64748b);
          font-size: 11.5px;
          line-height: 1.52;
          font-weight: 650;
        }

        body.dark .tasksProPage .tasksEmptyPremiumTitle,
        [data-theme="dark"] .tasksProPage .tasksEmptyPremiumTitle,
        body[data-theme="dark"] .tasksProPage .tasksEmptyPremiumTitle,
        body[data-tema="dark"] .tasksProPage .tasksEmptyPremiumTitle {
          color: #f8fafc;
        }

        body.dark .tasksProPage .tasksEmptyPremiumText,
        [data-theme="dark"] .tasksProPage .tasksEmptyPremiumText,
        body[data-theme="dark"] .tasksProPage .tasksEmptyPremiumText,
        body[data-tema="dark"] .tasksProPage .tasksEmptyPremiumText {
          color: rgba(203, 213, 225, 0.58);
        }

        @media (max-height: 700px) {
          .tasksProPage .tasksEmptyPremium { padding: 16px 18px; }
          .tasksProPage .tasksEmptyPremiumImage {
            width: 72px;
            height: 72px;
            margin-bottom: 12px;
          }
          .tasksProPage .tasksEmptyPremiumTitle { font-size: 14px; }
          .tasksProPage .tasksEmptyPremiumText { margin-top: 7px; font-size: 11.5px; }
        }

        .tasksProPage .taskListRow {
          align-items: center;
          gap: 9px;
          padding: 10px 11px;
        }

        .tasksProPage .taskListRow .tableRowContent {
          min-width: 0;
        }

        .tasksProPage .taskListRow .tableRowTitle {
          font-size: 14px;
          line-height: 1.28;
          letter-spacing: -0.01em;
          font-weight: 900;
        }

        .tasksProPage .taskListRow .tableRowSub {
          font-size: 11.5px;
          line-height: 1.35;
        }

        .tasksProPage .taskListRow .taskAciklama {
          font-size: 12.6px;
          line-height: 1.45;
        }
        .tasksProPage .taskListRow.hasDescription .taskAciklama {
          width: 100%;
          justify-content: flex-start;
          text-align: left;
          border: 0;
          background: transparent;
          padding: 2px 0 0;
          margin: 5px 0 0;
          border-radius: 0;
          color: color-mix(in srgb, var(--text-primary, #0f172a) 78%, var(--text-muted, #64748b));
          cursor: pointer;
          box-shadow: none;
        }

        .tasksProPage .taskListRow.hasDescription .taskAciklama span {
          display: block;
          white-space: normal;
          overflow-wrap: anywhere;
          font-weight: 720;
          letter-spacing: -0.005em;
        }

        .tasksProPage .taskListRow.hasDescription .taskAciklama small {
          display: inline-flex;
          margin-left: 5px;
          color: #6366f1;
          font-size: 10px;
          font-weight: 900;
        }

        .tasksProPage .taskListRow.hasDescription .taskAciklama.open {
          margin-top: 7px;
          padding: 2px 0 0;
          border-radius: 0;
          background: transparent;
          animation: taskDescriptionOpen 0.18s ease-out;
        }

        body.dark .tasksProPage .taskListRow.hasDescription .taskAciklama.open,
        [data-theme="dark"] .tasksProPage .taskListRow.hasDescription .taskAciklama.open,
        body[data-theme="dark"] .tasksProPage .taskListRow.hasDescription .taskAciklama.open,
        body[data-tema="dark"] .tasksProPage .taskListRow.hasDescription .taskAciklama.open {
          background: transparent;
          color: rgba(248,250,252,0.90);
        }

        @keyframes taskDescriptionOpen {
          from { opacity: 0; transform: translateY(-3px); }
          to { opacity: 1; transform: translateY(0); }
        }


        .tasksProPage .taskMetaBadges {
          gap: 5px;
          margin: 4px 0 3px;
        }

        .tasksProPage .oncelikBadge,
        .tasksProPage .kategoriTag,
        .tasksProPage .gecikmeTag {
          font-size: 9px;
          padding: 3px 7px;
          border-radius: 999px;
        }

        .tasksProPage .tableRowActions {
          gap: 6px;
          flex-shrink: 0;
        }

        .tasksProPage .checkButton {
          width: 32px;
          height: 32px;
          min-width: 32px;
        }

        .tasksProPage .rowDeleteBtn {
          min-height: 30px;
          padding: 0 10px;
          font-size: 11px;
          border-radius: 10px;
        }

        /* ── 2026 premium task UI ── */
        .tasksProPage .taskCreatePremiumHeader { grid-template-columns: 42px minmax(0,1fr) auto; gap: 11px; }
        .tasksProPage .taskCreatePremiumIcon { background: linear-gradient(135deg, color-mix(in srgb, var(--primary-color) 84%, #334155 16%), color-mix(in srgb, var(--primary-color) 72%, #ffffff 28%)); color:#fff !important; text-shadow:0 1px 2px rgba(15,23,42,.28); border:1px solid color-mix(in srgb, var(--primary-color) 78%, #ffffff 22%); box-shadow: 0 10px 22px color-mix(in srgb, var(--primary-color) 28%, transparent); }
        .tasksProPage .taskCreatePremiumHeading { min-width: 0; display: flex; flex-direction: column; gap: 2px; }
        .tasksProPage .taskCreatePremiumSubtitle { color: var(--text-muted); font-size: 10.5px; font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .tasksProPage .taskCreatePremiumArrow { color: var(--primary-color); }
        .tasksProPage .taskCreatePremiumAction { display:inline-flex; align-items:center; justify-content:center; gap:5px; min-height:30px; padding:0 10px; border-radius:999px; color:color-mix(in srgb, var(--primary-color) 82%, #1e293b 18%); background:color-mix(in srgb, var(--primary-color) 11%, transparent); border:1px solid color-mix(in srgb, var(--primary-color) 22%, transparent); font-size:10px; font-weight:850; white-space:nowrap; }
        .tasksProPage .taskCreatePremiumAction i { font-style:normal; font-size:15px; line-height:1; }
        .tasksProPage .taskCreatePremiumHeader:active .taskCreatePremiumAction { transform:scale(.97); }
        body.dark .tasksProPage .taskCreatePremiumAction, [data-theme="dark"] .tasksProPage .taskCreatePremiumAction, body[data-theme="dark"] .tasksProPage .taskCreatePremiumAction, body[data-tema="dark"] .tasksProPage .taskCreatePremiumAction { color:color-mix(in srgb, var(--primary-color) 64%, #ffffff 36%); background:color-mix(in srgb, var(--primary-color) 16%, transparent); }
        .tasksProPage .taskCreateSubmit:disabled { opacity: .48; cursor: not-allowed; filter: saturate(.45); }
        .tasksProPage .tasksOverview { flex: 0 0 auto; padding: 9px 12px 8px; border: 1px solid color-mix(in srgb, var(--primary-color) 15%, var(--border-color)); border-radius: 15px; background: color-mix(in srgb, var(--card-bg) 92%, var(--primary-color) 8%); }
        .tasksProPage .tasksOverviewTop { display:flex; align-items:center; justify-content:space-between; gap:12px; }
        .tasksProPage .tasksOverviewTop div { min-width:0; display:flex; flex-direction:column; gap:1px; }
        .tasksProPage .tasksOverviewTop strong { color:var(--text-primary); font-size:12.5px; font-weight:850; }
        .tasksProPage .tasksOverviewTop span { color:var(--text-muted); font-size:10px; font-weight:650; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .tasksProPage .tasksOverviewTop b { color:var(--primary-color); font-size:15px; }
        .tasksProPage .tasksProgressTrack { height:4px; margin-top:7px; overflow:hidden; border-radius:999px; background:color-mix(in srgb, var(--text-muted) 15%, transparent); }
        .tasksProPage .tasksProgressTrack span { display:block; height:100%; min-width:0; border-radius:inherit; background:var(--primary-gradient, var(--primary-color)); transition:width .3s ease; }
        .tasksProPage .taskFilterChips .chip { display:inline-flex; align-items:center; gap:6px; }
        .tasksProPage .taskFilterChips .chip small { display:inline-flex; align-items:center; justify-content:center; min-width:18px; height:18px; padding:0 5px; border-radius:999px; background:color-mix(in srgb, currentColor 12%, transparent); font-size:9px; font-weight:850; }
        .tasksProPage .tasksListCard { position:relative; }
        .tasksProPage .taskListRow.modern { position:relative; display:grid; grid-template-columns:36px minmax(0,1fr); gap:10px; align-items:start; padding:13px 12px; overflow:visible; }
        .tasksProPage .taskListRow.modern.tableRowLate { box-shadow:inset 3px 0 0 #f87171; }
        .tasksProPage .taskStatusButton { width:34px; height:34px; border-radius:50%; border:2px solid color-mix(in srgb, var(--text-muted) 38%, transparent); background:transparent; color:white; font-size:16px; font-weight:900; margin-top:1px; }
        .tasksProPage .taskStatusButton.checked { border-color:transparent; background:linear-gradient(135deg,#10b981,#059669); box-shadow:0 7px 16px rgba(16,185,129,.22); }
        .tasksProPage .taskModernContent { min-width:0; }
        .tasksProPage .taskModernHeader { display:flex; align-items:flex-start; justify-content:space-between; gap:8px; }
        .tasksProPage .taskModernHeader .taskTitleExpandable { min-width:0; flex:1; margin:0; }
        .tasksProPage .taskModernHeader .rowDeleteBtn { flex:0 0 auto; min-height:30px; padding:0 10px; border-radius:10px; font-size:11px; }
        .tasksProPage .taskModernBadges { display:flex; align-items:center; flex-wrap:wrap; gap:5px; margin-top:7px; }
        .tasksProPage .taskModernBadges .oncelikBadge { display:inline-flex; align-items:center; gap:5px; }
        .tasksProPage .taskModernBadges .oncelikBadge i { width:6px; height:6px; border-radius:50%; background:currentColor; }
        .tasksProPage .taskLateBadge { padding:3px 7px; border-radius:999px; background:rgba(248,113,113,.13); color:#f87171; font-size:9px; font-weight:850; }
        .tasksProPage .taskModernFooter { display:flex; align-items:center; gap:7px; min-width:0; margin-top:8px; }
        .tasksProPage .taskAssigneeStack { display:inline-flex; flex:0 0 auto; }
        .tasksProPage .taskAssigneeStack > span { width:20px; height:22px; margin-left:-5px; border-radius:50%; overflow:hidden; border:1.5px solid var(--card-bg); }
        .tasksProPage .taskAssigneeStack > span:first-child { margin-left:0; }
        .tasksProPage .taskModernFooter .tableRowSub { min-width:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; color:var(--text-muted); font-size:10.8px; }
        .tasksProPage .tasksEmptyAction { margin-top:12px; border:0; border-radius:12px; padding:10px 15px; background:var(--primary-gradient, var(--primary-color)); color:#fff; font-size:11px; font-weight:850; }
        body[data-tema="light"] .tasksProPage .taskCreatePremiumSubtitle, body[data-tema="light"] .tasksProPage .tasksOverviewTop span, body[data-tema="light"] .tasksProPage .taskModernFooter .tableRowSub { color:#64748b; }
        @media (max-width:390px) { .tasksProPage .taskCreatePremiumSubtitle { max-width:210px; } .tasksProPage .tasksOverviewTop span { max-width:245px; } }

        /* ── Premium görev dinamikleri ── */
        .tasksProPage .taskCreatePremiumCard {
          border-color: color-mix(in srgb, var(--primary-color) 16%, var(--border-color));
          background:
            radial-gradient(circle at 92% 0%, color-mix(in srgb, var(--primary-color) 10%, transparent), transparent 39%),
            var(--card-bg);
          box-shadow: 0 10px 26px rgba(2, 6, 23, 0.12);
        }
        .tasksProPage .taskCreatePremiumHeader {
          grid-template-columns: 42px minmax(0, 1fr) auto;
          min-height: 62px;
          padding: 10px 12px;
        }
        .tasksProPage .taskCreatePremiumIcon {
          position: relative;
          z-index: 1;
          overflow: visible;
          background: var(--primary-color, #6366f1);
          color: #ffffff !important;
          border: 1px solid rgba(255,255,255,.34);
          box-shadow:
            0 0 0 3px rgba(255,255,255,.055),
            0 0 14px var(--primary-color, #6366f1),
            0 0 26px var(--primary-color, #6366f1),
            inset 0 1px 0 rgba(255,255,255,.28);
          -webkit-filter: drop-shadow(0 0 7px var(--primary-color, #6366f1));
          filter: drop-shadow(0 0 7px var(--primary-color, #6366f1));
          -webkit-transform: translateZ(0);
          transform: translateZ(0);
          -webkit-animation: taskCreateGlowSafe 2.8s ease-in-out infinite;
          animation: taskCreateGlowSafe 2.8s ease-in-out infinite;
        }
        .tasksProPage .taskCreatePremiumIcon::before {
          content: "";
          position: absolute;
          inset: -4px;
          z-index: 2;
          border-radius: 50%;
          border: 1px solid var(--primary-color, #6366f1);
          opacity: .42;
          pointer-events: none;
        }
        .tasksProPage .taskCreatePremiumAction {
          min-width: 68px;
          min-height: 34px;
          padding: 0 11px;
          gap: 7px;
          color: var(--primary-color, #8b5cf6);
          background: rgba(255,255,255,.055);
          border: 1px solid var(--primary-color, #8b5cf6);
          border-radius: 999px;
          font-size: 11.5px;
          font-weight: 900;
          line-height: 1;
          box-shadow: inset 0 1px 0 rgba(255,255,255,.08), 0 5px 14px rgba(2,6,23,.10);
          transition: transform .18s ease, background .18s ease, border-color .18s ease;
        }
        .tasksProPage .taskCreatePremiumAction > span {
          color: inherit;
          opacity: 1;
        }
        .tasksProPage .taskCreatePremiumAction i {
          color: var(--primary-color, #8b5cf6);
          font-size: 16px;
          transform: rotate(0deg);
          transition: transform .22s ease;
        }
        .tasksProPage .taskCreatePremiumAction.open i { transform: rotate(180deg); }
        body.dark .tasksProPage .taskCreatePremiumAction,
        [data-theme="dark"] .tasksProPage .taskCreatePremiumAction,
        body[data-theme="dark"] .tasksProPage .taskCreatePremiumAction,
        body[data-tema="dark"] .tasksProPage .taskCreatePremiumAction {
          color: rgba(248,250,252,.96);
          background: rgba(255,255,255,.075);
          border-color: var(--primary-color, #8b5cf6);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.08), 0 6px 16px rgba(0,0,0,.18);
        }
        .tasksProPage .tasksOverview {
          position: relative;
          overflow: hidden;
          padding: 10px 12px 9px;
          border-radius: 17px;
          border: 1px solid color-mix(in srgb, var(--primary-color) 17%, transparent);
          background:
            radial-gradient(circle at 92% 8%, color-mix(in srgb, var(--primary-color) 16%, transparent), transparent 37%),
            linear-gradient(145deg, color-mix(in srgb, var(--card-bg) 96%, #0f172a 4%), var(--card-bg));
          box-shadow: 0 10px 24px rgba(2,6,23,.14);
          transition: border-color .3s ease, box-shadow .3s ease;
        }
        .tasksProPage .tasksOverview.isComplete {
          border-color: rgba(16,185,129,.20);
          box-shadow: 0 10px 26px rgba(16,185,129,.08);
        }
        .tasksProPage .tasksOverviewTop { align-items:flex-start; }
        .tasksProPage .tasksOverviewTop strong {
          font-size: 14px;
          line-height: 1.2;
          font-weight: 950;
          letter-spacing: -.015em;
        }
        .tasksProPage .tasksOverviewTop span {
          margin-top: 2px;
          font-size: 10.5px;
          line-height: 1.25;
          font-weight: 720;
        }
        .tasksProPage .tasksOverviewTop b {
          color: color-mix(in srgb, var(--primary-color) 64%, #ffffff 36%);
          font-size: 14px;
          line-height: 1.2;
          font-weight: 950;
        }
        .tasksProPage .tasksProgressTrack {
          position: relative;
          display: block;
          width: 100%;
          height: 5px;
          min-height: 5px;
          margin-top: 8px;
          overflow: hidden;
          border-radius: 999px;
          background: rgba(148,163,184,.16);
          box-shadow: inset 0 1px 2px rgba(2,6,23,.12);
        }
        .tasksProPage .tasksProgressTrack > span {
          display: block;
          height: 100%;
          min-height: 5px;
          border-radius: inherit;
          background: linear-gradient(90deg, var(--primary-color, #6366f1) 0%, var(--primary-color, #8b5cf6) 56%, #10b981 100%);
          box-shadow: 0 0 12px var(--primary-color, #8b5cf6);
          -webkit-transform: translateZ(0);
          transform: translateZ(0);
          transition: width .42s cubic-bezier(.2,.8,.2,1);
        }
        .tasksProPage .tasksOverviewStats {
          display:flex;
          align-items:center;
          gap:12px;
          margin-top:7px;
          color:var(--text-secondary);
          font-size:10px;
          font-weight:760;
        }
        .tasksProPage .tasksOverviewStats span { display:inline-flex; align-items:baseline; gap:4px; white-space:nowrap; }
        .tasksProPage .tasksOverviewStats b { color:var(--text-primary); font-size:12px; font-weight:950; }
        .tasksProPage .tasksOverviewStats .pending b { color:#fbbf24; }
        .tasksProPage .tasksOverviewStats .completed b { color:#34d399; }
        .tasksProPage .tasksCompleteBanner {
          position: relative;
          display:flex;
          align-items:center;
          gap:11px;
          margin:11px;
          padding:11px 12px;
          overflow:hidden;
          border:1px solid rgba(16,185,129,.20);
          border-radius:15px;
          background:
            radial-gradient(circle at 95% 0%, rgba(16,185,129,.12), transparent 42%),
            rgba(16,185,129,.05);
          animation: taskBannerIn .32s cubic-bezier(.2,.8,.2,1) both;
        }
        .tasksProPage .tasksCompleteIcon {
          width:36px;
          height:36px;
          flex:0 0 auto;
          display:inline-flex;
          align-items:center;
          justify-content:center;
          border-radius:12px;
          color:#6ee7b7;
          background:linear-gradient(145deg, rgba(16,185,129,.27), rgba(5,150,105,.13));
          box-shadow:inset 0 0 0 1px rgba(52,211,153,.13), 0 8px 18px rgba(16,185,129,.12);
          font-size:20px;
          font-weight:950;
        }
        .tasksProPage .tasksCompleteBanner h3 { margin:0; color:var(--text-primary); font-size:13px; font-weight:950; line-height:1.2; }
        .tasksProPage .tasksCompleteBanner p { margin:3px 0 0; color:var(--text-secondary); font-size:10.5px; font-weight:680; line-height:1.35; }
        .tasksProPage .tasksCompleteSpark {
          position:absolute;
          color:#6ee7b7;
          opacity:0;
          font-size:12px;
          pointer-events:none;
        }
        .tasksProPage .tasksCompleteSpark.sparkOne { right:21px; top:10px; }
        .tasksProPage .tasksCompleteSpark.sparkTwo { right:44px; bottom:8px; font-size:9px; }
        .tasksProPage .tasksCompleteBanner.celebrate .tasksCompleteSpark { animation: taskSparkle 1.2s ease-in-out 1; }
        .tasksProPage .tasksCompleteBanner.celebrate .tasksCompleteIcon { animation: taskCompletePop .6s cubic-bezier(.2,.9,.2,1.3) 1; }
        .tasksProPage .taskListRow.modern {
          animation: taskRowIn .28s cubic-bezier(.2,.8,.2,1) both;
          animation-delay: calc(var(--task-index, 0) * 24ms);
          transition: opacity .2s ease, transform .2s ease, background .2s ease;
        }
        .tasksProPage .taskListRow.modern.isUpdating { opacity:.58; transform:scale(.99); pointer-events:none; }
        .tasksProPage .taskStatusButton { transition: transform .18s ease, border-color .18s ease, background .18s ease, box-shadow .18s ease; }
        .tasksProPage .taskStatusButton:active { transform:scale(.86); transition-duration:.08s; }
        .tasksProPage .taskStatusButton.checked { animation:taskCheckPop .32s cubic-bezier(.2,.9,.2,1.3); }
        .tasksProPage .taskStatusButton:disabled { cursor:wait; }
        body[data-tema="light"] .tasksProPage .taskCreatePremiumCard {
          background:
            radial-gradient(circle at 92% 0%, color-mix(in srgb, var(--primary-color) 8%, transparent), transparent 40%),
            #ffffff;
          box-shadow:0 9px 23px color-mix(in srgb, var(--primary-color) 8%, transparent);
        }
        body[data-tema="light"] .tasksProPage .taskCreatePremiumAction {
          color: var(--primary-color, #6366f1);
          background: #ffffff;
          border-color: var(--primary-color, #6366f1);
          box-shadow: inset 0 1px 0 rgba(255,255,255,.9), 0 5px 14px rgba(15,23,42,.08);
        }
        body[data-tema="light"] .tasksProPage .tasksOverview {
          background:
            radial-gradient(circle at 92% 8%, color-mix(in srgb, var(--primary-color) 9%, transparent), transparent 38%),
            linear-gradient(145deg,#ffffff,color-mix(in srgb,#ffffff 96%,var(--primary-color) 4%));
          box-shadow:0 9px 22px color-mix(in srgb, var(--primary-color) 8%, transparent);
        }
        body[data-tema="light"] .tasksProPage .tasksOverviewTop b { color:var(--primary-color); }
        body[data-tema="light"] .tasksProPage .tasksCompleteBanner {
          border-color:rgba(5,150,105,.15);
          background:radial-gradient(circle at 95% 0%, rgba(16,185,129,.08), transparent 44%), rgba(16,185,129,.035);
        }
        body[data-tema="light"] .tasksProPage .tasksCompleteIcon { color:#059669; }
        @keyframes taskCreateGlow { 0%,100% { opacity:.22; transform:scale(.92); } 50% { opacity:.46; transform:scale(1.08); } }
        @-webkit-keyframes taskCreateGlowSafe {
          0%,100% { -webkit-filter: drop-shadow(0 0 5px var(--primary-color, #6366f1)); filter: drop-shadow(0 0 5px var(--primary-color, #6366f1)); }
          50% { -webkit-filter: drop-shadow(0 0 11px var(--primary-color, #6366f1)); filter: drop-shadow(0 0 11px var(--primary-color, #6366f1)); }
        }
        @keyframes taskCreateGlowSafe {
          0%,100% { -webkit-filter: drop-shadow(0 0 5px var(--primary-color, #6366f1)); filter: drop-shadow(0 0 5px var(--primary-color, #6366f1)); }
          50% { -webkit-filter: drop-shadow(0 0 11px var(--primary-color, #6366f1)); filter: drop-shadow(0 0 11px var(--primary-color, #6366f1)); }
        }
        @keyframes taskBannerIn { from { opacity:0; transform:translateY(-7px) scale(.985); } to { opacity:1; transform:none; } }
        @keyframes taskRowIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:none; } }
        @keyframes taskCheckPop { 0% { transform:scale(.82); } 65% { transform:scale(1.08); } 100% { transform:scale(1); } }
        @keyframes taskCompletePop { 0% { transform:scale(.72) rotate(-8deg); } 70% { transform:scale(1.12) rotate(3deg); } 100% { transform:scale(1) rotate(0); } }
        @keyframes taskSparkle { 0% { opacity:0; transform:scale(.4) rotate(0); } 45% { opacity:1; transform:scale(1.2) rotate(30deg); } 100% { opacity:0; transform:scale(.7) translateY(-8px) rotate(65deg); } }
        @media (prefers-reduced-motion: reduce) {
          .tasksProPage .taskCreatePremiumIcon,
          .tasksProPage .taskCreatePremiumIcon::before,
          .tasksProPage .taskListRow.modern,
          .tasksProPage .tasksCompleteBanner,
          .tasksProPage .tasksCompleteBanner.celebrate .tasksCompleteSpark,
          .tasksProPage .tasksCompleteBanner.celebrate .tasksCompleteIcon,
          .tasksProPage .taskStatusButton.checked { animation:none !important; }
          .tasksProPage .tasksProgressTrack span { transition:none; }
        }

        body.tasks-scroll-lock #root {
          height: 100dvh;
          max-height: 100dvh;
          overflow: hidden;
        }

        @media (max-height: 720px) {
          .tasksProPage { gap: 6px; }
          .tasksProPage .tasksListCard { flex: 1 1 auto; max-height: none; }
          .tasksProPage .tasksAddCard { padding: 9px 11px; }
          .tasksProPage .taskCreatePremiumHeader { grid-template-columns: 36px minmax(0, 1fr) auto; min-height: 54px; padding: 9px 11px; }
          .tasksProPage .taskCreatePremiumCard.open { max-height: min(78dvh, 560px); }
          .tasksProPage .taskCreatePremiumIcon { width: 36px; height: 36px; font-size: 23px; }
          .tasksProPage .tasksCompactForm { gap: 6px; }
          .tasksProPage .tasksCompactForm textarea.darkInput { min-height: 46px; }
          .tasksProPage .taskListRow { padding: 8px 10px; }
          .tasksProPage .taskListRow .tableRowTitle { font-size: 12.5px; }
          .tasksProPage .taskListRow .tableRowSub { font-size: 10.8px; }
          .tasksProPage .taskListRow .taskAciklama { font-size: 12px; }
        }

        /* ── Stage 3: responsive task flow and restrained hierarchy ── */
        .tasksProPage .taskCreatePremiumCard {
          border-color: color-mix(in srgb, var(--text-secondary) 22%, var(--border-color));
          background: var(--card-bg, var(--bg-elevated));
          box-shadow: 0 8px 22px rgba(15, 23, 42, .08);
        }

        .tasksProPage .taskCreatePremiumHeader {
          grid-template-columns: 34px minmax(0, 1fr) auto;
          min-height: 58px;
          gap: 10px;
          padding: 10px 12px;
        }

        .tasksProPage .taskCreatePremiumIcon {
          width: 34px;
          height: 34px;
          overflow: hidden;
          border-radius: 11px;
          border: 1px solid color-mix(in srgb, var(--primary-color) 24%, var(--border-color));
          background: color-mix(in srgb, var(--primary-color) 11%, var(--card-bg, var(--bg-elevated)));
          color: var(--primary-color) !important;
          font-size: 20px;
          font-weight: 800;
          text-shadow: none;
          box-shadow: none;
          filter: none;
          -webkit-filter: none;
          animation: none;
          -webkit-animation: none;
        }

        .tasksProPage .taskCreatePremiumIcon::before {
          display: none;
        }

        .tasksProPage .taskCreatePremiumAction {
          min-width: 0;
          min-height: 32px;
          padding: 0 10px;
          gap: 5px;
          border-radius: 10px;
          border-color: color-mix(in srgb, var(--primary-color) 28%, var(--border-color));
          background: color-mix(in srgb, var(--primary-color) 7%, var(--card-bg, var(--bg-elevated)));
          color: color-mix(in srgb, var(--primary-color) 78%, var(--text-primary) 22%);
          box-shadow: none;
          font-size: 10.5px;
          font-weight: 850;
        }

        .tasksProPage .taskCreatePremiumAction i {
          font-size: 13px;
        }

        body[data-tema="light"] .tasksProPage .taskCreatePremiumCard {
          background: #ffffff;
          border-color: rgba(100, 116, 139, .20);
          box-shadow: 0 8px 20px rgba(15, 23, 42, .06);
        }

        body[data-tema="light"] .tasksProPage .taskCreatePremiumAction {
          background: #f8faff;
          border-color: color-mix(in srgb, var(--primary-color) 25%, #dbe3ef);
          box-shadow: none;
        }

        body[data-tema="dark"] .tasksProPage .taskCreatePremiumIcon {
          color: #c4b5fd !important;
          background: rgba(124, 92, 246, .12);
          border-color: rgba(167, 139, 250, .30);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, .045);
        }

        body[data-tema="dark"] .tasksProPage .tasksCompactForm .darkInput:not(.formFieldInvalid) {
          color: #e5e7eb;
          background: rgba(15, 23, 42, .38);
          border-color: rgba(148, 163, 184, .18);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, .025);
        }

        body[data-tema="dark"] .tasksProPage .tasksCompactForm .darkInput:not(.formFieldInvalid):focus {
          background: rgba(30, 41, 59, .52);
          border-color: rgba(167, 139, 250, .58);
          box-shadow: 0 0 0 3px rgba(139, 92, 246, .12), inset 0 1px 0 rgba(255, 255, 255, .035);
        }

        body[data-tema="dark"] .tasksProPage .tasksCompactForm .darkInput::placeholder {
          color: rgba(148, 163, 184, .72);
          opacity: 1;
        }

        body[data-tema="dark"] .tasksProPage .tasksCompactForm .gorevAtananSecici:not(.formFieldInvalid) {
          border-color: rgba(148, 163, 184, .16);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, .03), 0 10px 24px rgba(2, 6, 23, .14);
        }

        body[data-tema="dark"] .tasksProPage .tasksCompactForm .reminderPanel {
          background: rgba(15, 23, 42, .30);
          border-color: rgba(167, 139, 250, .25);
          box-shadow: inset 0 1px 0 rgba(255, 255, 255, .025);
        }

        body[data-tema="dark"] .tasksProPage .tasksCompactForm .reminderPanel:has(.reminderCheckLabel input:checked) {
          background: rgba(124, 58, 237, .075);
          border-color: rgba(167, 139, 250, .38);
        }

        .tasksProPage .tasksOverview,
        .tasksProPage .tasksListCard {
          border-color: color-mix(in srgb, var(--text-secondary) 20%, var(--border-color));
        }

        .tasksProPage .tasksOverviewTop span,
        .tasksProPage .tasksOverviewStats,
        .tasksProPage .taskModernFooter .tableRowSub,
        .tasksProPage .tasksEmptyPremiumText {
          color: var(--text-secondary);
          opacity: 1;
        }

        .tasksProPage .taskFilterChips {
          display: flex;
          flex-wrap: nowrap;
          overflow-x: auto;
          overflow-y: hidden;
          scroll-snap-type: x proximity;
          scroll-padding-inline: 4px;
          padding: 3px 4px 8px;
          -webkit-overflow-scrolling: touch;
          scrollbar-width: none;
          mask-image: linear-gradient(90deg, transparent 0, #000 10px, #000 calc(100% - 10px), transparent 100%);
          -webkit-mask-image: linear-gradient(90deg, transparent 0, #000 10px, #000 calc(100% - 10px), transparent 100%);
        }

        .tasksProPage .taskFilterChips::-webkit-scrollbar { display: none; }
        .tasksProPage .taskFilterChips .chip { flex: 0 0 auto; scroll-snap-align: start; }
        .tasksProPage .taskFilterChips .chip:focus-visible,
        .tasksProPage .taskCreatePremiumHeader:focus-visible,
        .tasksProPage .taskStatusButton:focus-visible,
        .tasksProPage .rowDeleteBtn:focus-visible {
          outline: 3px solid color-mix(in srgb, var(--primary-color) 30%, transparent);
          outline-offset: 2px;
        }

        .tasksProPage .taskListRow.modern {
          border-bottom-color: color-mix(in srgb, var(--text-secondary) 16%, var(--border-color));
          background: var(--card-bg, var(--bg-elevated));
        }

        body[data-tema="dark"] .tasksProPage .tasksListCard {
          border: 1px solid rgba(148, 163, 184, .16);
          background: rgba(15, 23, 42, .34);
          box-shadow: 0 12px 28px rgba(2, 6, 23, .16), inset 0 1px 0 rgba(255, 255, 255, .025);
        }

        body[data-tema="dark"] .tasksProPage .taskListRow.modern {
          border-bottom-color: rgba(148, 163, 184, .13);
          background: rgba(15, 23, 42, .20);
        }

        body[data-tema="dark"] .tasksProPage .taskListRow.modern:last-child {
          border-bottom-color: transparent;
        }

        .tasksProPage .taskListRow.modern.withoutStatus {
          grid-template-columns: minmax(0, 1fr);
        }

        .tasksProPage .taskListRow.modern.tableRowDone {
          opacity: 1;
          background: color-mix(in srgb, #10b981 4%, var(--card-bg, var(--bg-elevated)));
        }

        .tasksProPage .taskListRow.modern.tableRowDone .tableRowTitle,
        .tasksProPage .taskListRow.modern.tableRowDone .taskAciklama {
          color: var(--text-secondary);
        }

        .tasksProPage .taskStatusButton {
          width: 32px;
          height: 32px;
          border-color: color-mix(in srgb, var(--text-secondary) 48%, transparent);
        }

        .tasksProPage .taskModernHeader .rowDeleteBtn {
          min-height: 28px;
          padding: 0 9px;
          color: #b4232f;
          background: color-mix(in srgb, #ef4444 10%, var(--card-bg, var(--bg-elevated)));
          border: 1px solid color-mix(in srgb, #ef4444 22%, transparent);
        }

        body[data-tema="dark"] .tasksProPage .taskModernHeader .rowDeleteBtn {
          color: #fecaca;
          background: rgba(239, 68, 68, .15);
          border-color: rgba(248, 113, 113, .24);
        }

        .tasksProPage .taskLateBadge,
        .tasksProPage .dateRed {
          color: #dc2638;
        }

        body[data-tema="dark"] .tasksProPage .taskLateBadge,
        body[data-tema="dark"] .tasksProPage .dateRed {
          color: #fda4af;
        }

        .tasksProPage .tasksCompactForm .darkInput,
        .tasksProPage .gorevAtananSecici,
        .tasksProPage .tasksCompactForm .reminderPanel {
          border-color: color-mix(in srgb, var(--text-secondary) 28%, var(--border-color));
        }

        .tasksProPage .tasksCompactForm .darkLabel,
        .tasksProPage .gorevAtananToggleSummary,
        .tasksProPage .gorevAtananBosUyari,
        .tasksProPage .reminderCheckText {
          color: var(--text-secondary);
          opacity: 1;
        }

        .tasksProPage .tasksCompactForm input[type="date"].darkInput,
        .tasksProPage .tasksCompactForm input[type="time"].darkInput {
          color: var(--text-primary);
          -webkit-text-fill-color: var(--text-primary);
          color-scheme: light dark;
        }

        .tasksProPage .taskCreateSubmit {
          position: sticky;
          bottom: 0;
          z-index: 4;
          box-shadow: 0 8px 22px color-mix(in srgb, var(--primary-color) 22%, transparent);
        }

        .swipePageWrap.tasksTransitionGuard.tasksCompactPageScroll {
          min-height: 100dvh;
          max-height: none;
          overflow: visible;
          touch-action: pan-y;
          overscroll-behavior-y: contain;
        }

        .swipePageWrap.tasksTransitionGuard.tasksCompactPageScroll::before {
          bottom: 0;
        }

        .swipePageWrap.tasksCompactPageScroll .tasksProPage {
          height: auto;
          max-height: none;
          min-height: 0;
          overflow: visible;
          padding-bottom: max(24px, calc(env(safe-area-inset-bottom, 0px) + 14px));
        }

        .swipePageWrap.tasksCompactPageScroll .taskCreatePremiumCard.open {
          max-height: none;
        }

        .swipePageWrap.tasksCompactPageScroll .taskCreatePremiumCard.open .taskCreatePremiumBody,
        .swipePageWrap.tasksCompactPageScroll .tasksListCard {
          flex: none;
          max-height: none;
          overflow: visible;
        }

        .swipePageWrap.tasksCompactPageScroll .tasksListCard {
          min-height: 210px;
        }

        @media (max-width: 390px), (max-height: 720px) {
          .tasksProPage .tasksOverviewStats {
            gap: 8px;
            justify-content: space-between;
          }

          .tasksProPage .tasksOverviewStats span {
            gap: 3px;
            font-size: 9.5px;
          }

          .tasksProPage .tasksCompactForm .formRow,
          .tasksProPage .reminderSettings .formRow {
            display: grid;
            grid-template-columns: 1fr;
            align-items: stretch;
          }

          .tasksProPage .tasksCompactForm .formRow .darkInput,
          .tasksProPage .reminderSettings .formRow .darkInput {
            width: 100%;
            height: 44px;
            min-height: 44px;
            line-height: normal;
          }

          .tasksProPage .taskCreatePremiumSubtitle {
            max-width: none;
          }

          .tasksProPage .taskModernFooter {
            align-items: flex-start;
          }

          .tasksProPage .taskModernFooter .tableRowSub {
            white-space: normal;
            overflow-wrap: anywhere;
          }
        }
      `}</style>

      {/* ── GÖREV OLUŞTUR ── */}
      {canAdd && (
        <div className={`taskCreatePremiumCard ${gorevFormAcik ? "open" : "closed"}`}>
          <button
            className="taskCreatePremiumHeader"
            type="button"
            onClick={() => setGorevFormAcik(!gorevFormAcik)}
            aria-expanded={gorevFormAcik}
            aria-controls="task-create-form"
            aria-label={gorevFormAcik ? t("tasks.closeFormAction") : t("tasks.openFormAction")}
          >
            <span className="taskCreatePremiumIcon" aria-hidden="true">＋</span>
            <span className="taskCreatePremiumHeading">
              <span className="taskCreatePremiumTitle">{t("tasks.create")}</span>
              <span className="taskCreatePremiumSubtitle">{t("tasks.createSubtitle")}</span>
            </span>
            <span className={`taskCreatePremiumAction ${gorevFormAcik ? "open" : ""}`} aria-hidden="true"><span>{gorevFormAcik ? t("tasks.closeFormAction") : t("tasks.openFormAction")}</span><i>⌄</i></span>
          </button>
          {gorevFormAcik && (
            <div id="task-create-form" ref={taskFormBodyRef} className="taskCreatePremiumBody formBody shoppingCompactForm tasksCompactForm">
              <input className={taskValidation.fieldClass("name", "darkInput")} data-required-field="task-create-name" aria-invalid={taskValidation.hasError("name")} placeholder={t("tasks.name")} value={gorevAdi} onChange={(e) => { setGorevAdi(e.target.value); taskValidation.clearError("name"); }} />
              <textarea className="darkInput" placeholder={t("tasks.description")} value={gorevAciklama} onChange={(e) => setGorevAciklama(e.target.value)} rows={2} />
              <div className="inputLabelGroup">
                <label className="darkLabel">{t("common.assignee")}</label>
                <div className={taskValidation.fieldClass("assignees", `gorevAtananSecici ${taskAssigneeOpen ? "open" : "closed"}`)} data-required-field="task-create-assignees" role="group" aria-label={t("common.assignee")}>
                  <button
                    type="button"
                    className="gorevAtananToggle"
                    onClick={() => setTaskAssigneeOpen(prev => !prev)}
                    aria-expanded={taskAssigneeOpen}
                  >
                    <span className="gorevAtananToggleLeft">
                      <span className="gorevAtananToggleTitle">{taskAssigneeText.chooseTitle}</span>
                      <span className="gorevAtananToggleSummary">{gorevAtananOzeti}</span>
                    </span>
                    <span className="gorevAtananToggleAvatars" aria-hidden="true">
                      {seciliGorevUyeleri.slice(0, 3).map(u => (
                        <span className="gorevAtananMiniAvatar" key={taskMemberId(u)}>
                          <AvatarImg
                            avatar={u.avatar || u.avatarId || u.profilAvatar}
                            renk={u.renk || u.color || u.avatarRenk || u.profilRenk || "#6366f1"}
                            initials={taskMemberInitials(u)}
                            size={25}
                          
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
                        </span>
                      ))}
                      {seciliGorevUyeleri.length > 3 && <span className="gorevAtananMiniMore">+{seciliGorevUyeleri.length - 3}</span>}
                    </span>
                    <span className={`gorevAtananChevron ${taskAssigneeOpen ? "open" : ""}`} aria-hidden="true">⌄</span>
                  </button>

                  {taskAssigneeOpen && (
                    <div className="gorevAtananDropdown">
                      <button
                        type="button"
                        className={`gorevAtananOption all ${gorevAtananlar.length === siraliUyeler.length && siraliUyeler.length > 0 ? "secili" : ""}`}
                        onClick={() => { setGorevAtananlar(prev => prev.length === siraliUyeler.length ? [] : siraliUyeler.map(taskMemberId)); taskValidation.clearError("assignees"); }}
                      >
                        <span className="gorevAtananCheck">{gorevAtananlar.length === siraliUyeler.length && siraliUyeler.length > 0 ? "✓" : ""}</span>
                        <span className="gorevAtananText">
                          <strong>{taskAssigneeText.allFamily}</strong>
                          <small>{taskAssigneeText.allFamilyDesc}</small>
                        </span>
                        <span className="gorevAtananGo" aria-hidden="true">›</span>
                      </button>

                      <div className="gorevAtananMemberScroll" role="list">
                        {siraliUyeler.map((u) => {
                          const uyeUid = taskMemberId(u);
                          const secili = (gorevAtananlar || []).map(String).includes(uyeUid);
                          const role = taskNormalizeRole(u);
                          return (
                            <button
                              key={uyeUid}
                              type="button"
                              className={`gorevAtananOption ${secili ? "secili" : ""}`}
                              onClick={() => {
                                taskValidation.clearError("assignees");
                                setGorevAtananlar(prev => {
                                  const current = (prev || []).map(String);
                                  return current.includes(uyeUid)
                                    ? current.filter(id => id !== uyeUid)
                                    : [...current, uyeUid];
                                });
                              }}
                            >
                              <span className="gorevAtananCheck">{secili ? "✓" : ""}</span>
                              <span className="gorevAtananAvatar">
                                <AvatarImg
                                  avatar={u.avatar || u.avatarId || u.profilAvatar}
                                  renk={u.renk || u.color || u.avatarRenk || u.profilRenk || "#6366f1"}
                                  initials={taskMemberInitials(u)}
                                  size={36}
                                  radius="14px"
                                
                      uid={u.uid || u.id}
                      isOnline={u.isOnline === true || u.online === true}
                    />
                              </span>
                              <span className="gorevAtananText">
                                <strong>{taskMemberName(u)}</strong>
                                <small>{taskRoleLabel(role)}</small>
                              </span>
                              <span className="gorevAtananGo" aria-hidden="true">›</span>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
                {(gorevAtananlar || []).length === 0 && (
                  <span className="gorevAtananBosUyari">{t("tasks.selectPerson")}</span>
                )}              </div>
              <div className="formRow">
                <div className="inputLabelGroup">
                  <label className="darkLabel" htmlFor="task-create-due-date">{t("common.dueDate")}</label>
                  <input id="task-create-due-date" className={taskValidation.fieldClass("dueDate", "darkInput")} data-required-field="task-create-dueDate" aria-invalid={taskValidation.hasError("dueDate")} type="date" value={gorevSonTarih} onChange={(e) => { setGorevSonTarih(e.target.value); taskValidation.clearError("dueDate"); }} />
                </div>
                <div className="inputLabelGroup">
                  <label className="darkLabel" htmlFor="task-create-priority">{t("common.priority")}</label>
                  <select id="task-create-priority" className="darkInput" value={gorevOncelik} onChange={(e) => setGorevOncelik(e.target.value)}>
                    <option value="Düşük">{t("tasks.priority.low")}</option>
                    <option value="Normal">{t("tasks.priority.normal")}</option>
                    <option value="Yüksek">{t("tasks.priority.high")}</option>
                  </select>
                </div>
              </div>
              <div className="inputLabelGroup">
                <label className="darkLabel" htmlFor="task-create-category">{t("common.category")}</label>
                <select id="task-create-category" className="darkInput" value={gorevKategori || "Diğer"} onChange={(e) => setGorevKategori?.(e.target.value)}>
                  {GOREV_KAT_LISTESI.map((k) => <option key={k} value={k}>{gorevKategoriEtiketi(k)}</option>)}
                </select>
                {(gorevKategori === "Sağlık" || gorevKategori === "Randevu" ||
                  gorevKategori === "Health" || gorevKategori === "Appointment") && (
                  <div className="gorevSaglikHint">
                    {t("tasks.healthHint")}
                  </div>
                )}
              </div>
              <div ref={taskReminderRef}>
                <ReminderPanel
                  aktif={reminderAktif}  setAktif={setReminderAktif}
                  tarih={reminderTarih}  setTarih={setReminderTarih}
                  saat={reminderSaat}    setSaat={setReminderSaat}
                  tekrar={reminderTekrar} setTekrar={setReminderTekrar}
                  mod="datetime"
                  etiket={t("tasks.reminder")}
                />
              </div>
              <ReminderWarning message={reminderAktif ? reminderError : ""} />
              <FormValidationNotice
                errors={taskValidation.errors}
                labels={{ name: t("tasks.name"), assignees: t("common.assignee"), dueDate: t("common.dueDate") }}
              />
              <AsyncActionButton
                className="primaryButton taskCreateSubmit"
                onAction={handleGorevEkle}
                externalBusy={taskCreating}
                busyText={t("tasks.creating")}
                disabled={taskCreating}
              >
                {t("tasks.create")}
              </AsyncActionButton>
            </div>
          )}
        </div>
      )}

      {taskCounts.total > 0 && (
        <section className={`tasksOverview ${allTasksCompleted ? "isComplete" : ""}`} aria-label={t("tasks.overview")}>
          <div className="tasksOverviewTop">
            <div>
              <strong>{t("tasks.overview")}</strong>
              <span>{taskProgressMessage}</span>
            </div>
            <b>{completionPercent}%</b>
          </div>
          <div
            className="tasksProgressTrack"
            role="progressbar"
            aria-valuemin="0"
            aria-valuemax="100"
            aria-valuenow={completionPercent}
          >
            <span style={{ width: `${completionPercent}%` }} />
          </div>
          <div className="tasksOverviewStats">
            <span><b>{taskCounts.total}</b> {t("tasks.stats.total")}</span>
            <span className="pending"><b>{taskCounts.active}</b> {t("tasks.stats.pending")}</span>
            <span className="completed"><b>{taskCounts.done}</b> {t("tasks.stats.completed")}</span>
          </div>
        </section>
      )}

      {/* ── FİLTRELER ── */}
      <div className="categoryChips taskFilterChips noSwipeBack" data-no-swipe-back="true" role="group" aria-label={t("tasks.overview")}>
        {FILTERS.map((f) => (
          <button key={f.value} type="button" aria-pressed={filtreCanonical(gorevFiltresi) === f.value} className={filtreCanonical(gorevFiltresi) === f.value ? "chip activeBlue" : "chip"} onClick={() => setGorevFiltresi(f.value)}>
            <span>{f.label}</span><small>{filterCount(f.value)}</small>
          </button>
        ))}
      </div>

      {/* ── GÖREV LİSTESİ ── */}
      <div className="tableCard tasksListCard">
        {sirali.length === 0 ? (
          <div className="tasksEmptyPremium" role="status" aria-live="polite">
            <img
              className="tasksEmptyPremiumImage"
              src={gorevlerAsset}
              alt=""
              aria-hidden="true"
              loading="eager"
              decoding="sync"
              fetchPriority="high"
              draggable="false"
            />
            <h3 className="tasksEmptyPremiumTitle">{emptyCopy.title}</h3>
            <p className="tasksEmptyPremiumText">{emptyCopy.text}</p>
            {canAdd && filtreCanonical(gorevFiltresi) === "Tümü" && (
              <button type="button" className="tasksEmptyAction" onClick={() => setGorevFormAcik(true)}>
                {t("tasks.createFirst")}
              </button>
            )}
          </div>
        ) : (
          <>
            {allTasksCompleted && filtreCanonical(gorevFiltresi) === "Tümü" && (
              <div className={`tasksCompleteBanner ${completionPulse ? "celebrate" : ""}`} role="status" aria-live="polite">
                <span className="tasksCompleteIcon" aria-hidden="true">✓</span>
                <div>
                  <h3>{t("tasks.completeBannerTitle")}</h3>
                  <p>{t("tasks.completeBannerText")}</p>
                </div>
                <i className="tasksCompleteSpark sparkOne" aria-hidden="true">✦</i>
                <i className="tasksCompleteSpark sparkTwo" aria-hidden="true">✦</i>
              </div>
            )}
            {gorunenGorevler.map((g, idx) => {
              const canComplete = canCompleteAll || (canCompleteOwn && gorevKullaniciyaAtanmisMi(g));
              const canDelete = canDeleteAll || (canDeleteOwn && g.ekleyenUid === user?.uid);
              const assignees = taskAssigneesForCard(g);
              const assigneeLabel = gorevAtananKartMetni(g);
              const lateDays = overdueDays(g);
              return (
                <article key={g.id}
                  style={{ "--task-index": idx }}
                  className={`tableRow taskListRow modern ${canComplete ? "" : "withoutStatus"} ${g.aciklama ? "hasDescription" : ""} ${gecikmisMi(g) ? "tableRowLate" : ""} ${g.tamamlandi ? "tableRowDone" : ""} ${taskUpdatingId === g.id ? "isUpdating" : ""} ${idx === gorunenGorevler.length - 1 ? "tableRowLast" : ""}`}>
                  {canComplete && (
                    <button
                      type="button"
                      className={g.tamamlandi ? "taskStatusButton checked" : "taskStatusButton"}
                      onClick={() => handleTaskToggle(g)}
                      disabled={taskUpdatingId === g.id}
                      aria-busy={taskUpdatingId === g.id}
                      aria-label={g.tamamlandi ? t("tasks.reopen") : t("tasks.complete")}
                    >
                      {g.tamamlandi ? "✓" : ""}
                    </button>
                  )}
                  <div className="tableRowContent taskModernContent">
                    <div className="taskModernHeader">
                      <ExpandableText text={g.ad} maxLength={42} className={g.tamamlandi ? "tableRowTitle taskTitleExpandable strike" : "tableRowTitle taskTitleExpandable"} />
                      {canDelete && <button type="button" className="rowDeleteBtn" onClick={() => handleTaskDelete(g)}>{t("common.delete")}</button>}
                    </div>
                    {g.aciklama && <ExpandableText text={g.aciklama} maxLength={72} className="taskAciklama taskDescriptionExpandable" />}
                    <div className="taskModernBadges">
                      <span className={`oncelikBadge ${oncelikRenk[oncelikCanonical(g.oncelik)] || "oncelikNormal"}`}><i />{oncelikEtiketi(g.oncelik)}</span>
                      {g.kategori && g.kategori !== t("common.other") && g.kategori !== "Diğer" && <span className="kategoriTag">{gorevKategoriEtiketi(g.kategori)}</span>}
                      {lateDays > 0 && <span className="taskLateBadge">{t("tasks.daysLate", { count: lateDays })}</span>}
                    </div>
                    {(assignees.length > 0 || assigneeLabel || g.sonTarih) && (
                      <div className="taskModernFooter">
                        {assignees.length > 0 && <span className="taskAssigneeStack" aria-hidden="true">
                          {assignees.slice(0, 3).map((u) => <span key={taskMemberId(u)}><AvatarImg avatar={u.avatar || u.avatarId || u.profilAvatar} renk={u.renk || u.color || u.avatarRenk || u.profilRenk || "var(--primary-color)"} initials={taskMemberInitials(u)} size={22} uid={u.uid || u.id} isOnline={u.isOnline === true || u.online === true} /></span>)}
                        </span>}
                        <span className="tableRowSub">
                          {assigneeLabel}
                          {assigneeLabel && g.sonTarih && " · "}
                          {g.sonTarih && <span className={gecikmisMi(g) ? "dateRed" : ""}>{tarihOkunakli(g.sonTarih)}</span>}
                        </span>
                      </div>
                    )}
                  </div>
                </article>
              );
            })}
          </>
        )}
      </div>

    </div>
  );
}

export default TasksPage;
